import { useMemo } from 'react';

/**
 * Assigns a value to a React ref, handling both function callbacks and mutable ref objects.
 *
 * @template T
 * @param {React.Ref<T> | null | undefined} ref - The ref to be updated.
 * @param {T} value - The DOM node or component instance to assign to the ref.
 */
const setRef = (ref, value) => {
    if (typeof ref === 'function') ref(value);
    else if (ref) ref.current = value;
};

/**
 * A React hook that combines multiple refs into a single callback ref.
 * Useful when you need to attach a forwarded ref from a parent
 * to a DOM node, while also maintaining a local ref to the same node within your component.
 *
 * @template T
 * @param {...(React.Ref<T> | null | undefined)} refs - An array of refs to be merged.
 * @returns {React.RefCallback<T> | null} A single callback ref that updates all provided refs, or null if all inputs are nullish.
 */
export const useForkRef = (...refs) => {
    return useMemo(() => {
        if (refs.every((ref) => ref == null)) {
            return null;
        }

        return (instance) => {
            refs.forEach((ref) => {
                setRef(ref, instance);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, refs);
};
